import { isNil } from 'lodash';
import { useContext } from 'react';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { CardTemplateContext } from '@shared/components/blocks/contexts/template';
import { useWorkflowEntitiesByIds } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import {
  isAddressComponent,
  isDateComponent,
  isEmailComponent,
  isInputComponent,
  isNumberComponent,
  isRelatedCardLookupComponent,
  isTagComponent,
  isTextComponent,
} from '../../utils';
import { WorkflowAddressComponent } from './WorkflowAddressComponent';
import { WorkflowComponentProps } from './WorkflowComponent';
import { WorkflowDateComponent } from './WorkflowDateComponent';
import { LookupEmailComponent } from './WorkflowEmailComponent';
import { WorkflowNumberComponent } from './WorkflowNumberComponent';
import { WorkflowTagComponent } from './WorkflowTagComponent';
import { WorkflowTextComponent } from './WorkflowTextComponent';

export function WorkflowLookupComponentLowCode(props: WorkflowComponentProps) {
  const { values: fields } = useCardFormContext();
  const { template } = useContext(CardTemplateContext);
  const renderContext = useContext(CardRenderContext);
  const relatedCardComponent = isRelatedCardLookupComponent(props.component)
    ? fields[props.component.relatedCardComponentId]
    : null;
  const lookupEntityIds = relatedCardComponent?.map(c => c.id);
  const isExternalForm =
    ['nocode-public-create', 'nocode-public-readonly'].includes(template?.context ?? '') ||
    template?.context === 'create-external';
  const isCollectionView = ['list', 'table'].includes(renderContext);

  return (
    <WorkflowLookupComponent
      {...props}
      lookupEntityIds={lookupEntityIds}
      isExternalForm={isExternalForm}
      isCollectionView={isCollectionView}
    />
  );
}

export function WorkflowLookupComponent({
  component,
  showLabel,
  showIcon,
  paddingHorizontal,
  iconPad,
  overrides,
  lookupEntityIds,
  isExternalForm,
  isCollectionView,
}: WorkflowComponentProps & {
  lookupEntityIds: string[];
  isExternalForm: boolean;
  isCollectionView: boolean;
}) {
  if (!isRelatedCardLookupComponent(component)) {
    throw new Error('Component is not a related card lookup component');
  }
  const { workflowTemplate: relatedCardsWorkflowTemplate, workflowEntities: relatedCards } =
    useWorkflowEntitiesByIds({
      workflowTemplateId: component.lookupWorkflowTemplateId,
      workflowEntityIds: lookupEntityIds,
    });
  const relatedCardsInDisplayOrder = lookupEntityIds
    ?.map(c => relatedCards.find(r => r.id === c))
    .filter(c => !isNil(c));
  const rawValues = relatedCardsInDisplayOrder
    ?.map(c => c.fields?.[component.lookupComponentId])
    .filter(notNil);
  const lookupComponent = relatedCardsWorkflowTemplate?.components.find(
    c => c.id === component.lookupComponentId,
  );

  if (
    !isInputComponent(component) ||
    ((isNil(rawValues) || rawValues.length === 0) && isCollectionView) ||
    isExternalForm ||
    isNil(lookupComponent)
  ) {
    return null;
  }

  const label =
    !isNil(overrides) && 'label' in overrides && !isNil(overrides?.label)
      ? overrides.label
      : component.label;
  const icon =
    !isNil(overrides) && 'icon' in overrides && !isNil(overrides?.icon)
      ? overrides.icon
      : component.icon;
  const displayComponentValues = {
    label,
    icon,
    placeholder: component.placeholder,
    readonly: component.readonly,
  };

  const displayComponent = {
    ...lookupComponent,
    ...displayComponentValues,
  };

  if (isTextComponent(displayComponent)) {
    return (
      <WorkflowTextComponent
        initialData={rawValues}
        component={displayComponent}
        showLabel={showLabel}
        paddingHorizontal={paddingHorizontal}
        showIcon={showIcon}
        iconPad={iconPad}
      />
    );
  }

  if (isNumberComponent(displayComponent)) {
    return (
      <WorkflowNumberComponent
        initialData={rawValues}
        component={displayComponent}
        showLabel={showLabel}
        showIcon={showIcon}
        paddingHorizontal={paddingHorizontal}
        iconPad={iconPad}
      />
    );
  }

  if (isDateComponent(displayComponent)) {
    return (
      <WorkflowDateComponent
        initialData={rawValues}
        component={displayComponent}
        showLabel={showLabel}
        showIcon={showIcon}
        paddingHorizontal={paddingHorizontal}
        iconPad={iconPad}
      />
    );
  }

  if (isEmailComponent(displayComponent)) {
    return (
      <LookupEmailComponent
        initialData={rawValues}
        component={displayComponent}
        showLabel={showLabel}
        showIcon={showIcon}
        paddingHorizontal={paddingHorizontal}
        iconPad={iconPad}
      />
    );
  }

  if (isAddressComponent(displayComponent)) {
    return (
      <WorkflowAddressComponent
        initialData={rawValues}
        component={displayComponent}
        showLabel={showLabel}
        showIcon={showIcon}
        paddingHorizontal={paddingHorizontal}
        iconPad={iconPad}
      />
    );
  }

  if (isTagComponent(displayComponent)) {
    return (
      <WorkflowTagComponent
        initialData={rawValues}
        component={displayComponent}
        showLabel={showLabel}
        showIcon={showIcon}
        paddingHorizontal={paddingHorizontal}
        iconPad={iconPad}
      />
    );
  }

  if (!isNil((displayComponent as API.AnyComponent)?.type)) {
    console.error(
      'Unsupported lookup component type',
      (displayComponent as API.AnyComponent)?.type,
    );
  }

  return null;
}
